/**
 * Widget 输入区域组件
 *
 * 设计规范（来自 pencil）：
 * - 容器：56px 高，padding 8px 12px，背景 #FFFFFF，顶部边框 #E4E4E7
 * - 输入框：40px 高，圆角 20px，背景 #F4F4F5，padding 0 16px
 * - 占位符颜色：#A1A1AA，字体大小 14px
 * - 发送按钮：40×40px，圆角 20px，背景 #2563EB，send 图标 18×18px
 * - 底部圆角：16px
 *
 * 可访问性：
 * - input 关联 label
 * - 按钮有明确的 aria-label
 */

import React, { useState, KeyboardEvent, ChangeEvent } from 'react';
import { clsx } from 'clsx';

export interface WidgetInputAreaProps {
  /** 发送消息回调 */
  onSend: (content: string) => void;
  /** 是否禁用 */
  disabled?: boolean;
  /** 自定义占位符 */
  placeholder?: string;
  /** 自定义 CSS 类名 */
  className?: string;
}

export const WidgetInputArea: React.FC<WidgetInputAreaProps> = ({
  onSend,
  disabled = false,
  placeholder = '输入您的问题...',
  className,
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !disabled) {
      onSend(trimmed);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  return (
    <div
      className={clsx(
        'flex items-center gap-[8px]',
        'h-[56px]',
        'px-[12px]',
        'py-[8px]',
        'bg-white',
        'border-t border-[#E4E4E7]',
        'rounded-b-[16px]',
        className
      )}
      aria-label="消息输入区域"
    >
      {/* 输入框 */}
      <div className="flex-1 relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          className={clsx(
            'w-full h-[40px]',
            'px-[16px]',
            'rounded-full',
            'bg-[#F4F4F5]',
            'text-[14px] leading-[20px] text-[#18181B]',
            'placeholder:text-[#A1A1AA]',
            'border-none outline-none',
            'transition-all duration-200',
            'focus:ring-2 focus:ring-[#2563EB] focus:ring-opacity-50',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          aria-label="输入消息"
        />
      </div>

      {/* 发送按钮 */}
      <button
        type="button"
        onClick={handleSend}
        disabled={disabled || !inputValue.trim()}
        className={clsx(
          'flex-shrink-0',
          'w-[40px]',
          'h-[40px]',
          'rounded-full',
          'bg-[#2563EB]',
          'flex items-center justify-center',
          'transition-all duration-200',
          'hover:scale-105 active:scale-95',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-opacity-50',
          'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100'
        )}
        aria-label="发送消息"
      >
        {/* Lucide Send 图标 */}
        <svg
          className="w-[18px] h-[18px] text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 2L11 13" />
          <path d="M22 2l-7 20-4-9-9-4 20-7z" />
        </svg>
      </button>
    </div>
  );
};
